import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter } from "@angular/router";

import { firstValueFrom } from "rxjs";

import { authInterceptor } from "./core/interceptors/auth.interceptor";
import { csrfInterceptor } from "./core/interceptors/csrf.interceptor";
import { errorInterceptor } from "./core/interceptors/error.interceptor";
import { AuthService } from "./core/services/auth.service";
import { CsrfService } from "./core/services/csrf.service";
import { ThemeService } from "./core/services/theme.service";
import { appRoutes } from "./app.routes";

export function initCsrf(csrfService: CsrfService): () => Promise<void> {
  return () => csrfService.fetchCsrfToken();
}

export function initAuth(authService: AuthService): () => Promise<void> {
  return () =>
    firstValueFrom(authService.hydrateCurrentUser()).then(() => undefined);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([authInterceptor, csrfInterceptor, errorInterceptor]),
    ),
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: initCsrf,
      deps: [CsrfService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [AuthService],
      multi: true,
    },
    // Instantiates ThemeService at bootstrap so it takes over the theme class
    // from the index.html pre-bootstrap script (and stays authoritative).
    provideAppInitializer(() => {
      inject(ThemeService);
    }),
  ],
};
